import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Plus, Trash2, Upload, AlertTriangle, Pencil, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_COLORS = { Minor: 'secondary', Moderate: 'outline', Severe: 'default', Critical: 'destructive' };
const STATUS_COLORS = { Reported: 'outline', 'In Repair': 'secondary', Repaired: 'default', 'Written Off': 'destructive' };

const AssetDamageReports = ({ asset, isAdmin, onRefresh, config = {}, employees = [], navigateTo, onClose }) => {
  const { t } = useTranslation();
  const { damageSeverities = [], repairStatuses = [], currencies = [] } = config;
  const severityNames = damageSeverities.length > 0 ? damageSeverities.map(s => s.name) : ['Minor', 'Moderate', 'Severe', 'Critical'];
  const statusNames = repairStatuses.length > 0 ? repairStatuses.map(s => s.name) : ['Reported', 'In Repair', 'Repaired', 'Written Off'];
  const currencyList = currencies.length > 0 ? currencies : ['PLN', 'EUR', 'GBP', 'USD'];

  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const emptyForm = { date: '', description: '', severity: severityNames[0] || 'Minor', reported_by: '', repair_status: statusNames[0] || 'Reported', repair_notes: '', repair_cost: '', repair_currency: 'PLN' };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploadingFor, setUploadingFor] = useState(null);
  const [picUrls, setPicUrls] = useState({});
  const loadedRef = useRef(new Set());
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewPhotoMeta, setPreviewPhotoMeta] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  React.useEffect(() => {
    const reports = asset.damage_reports || [];
    reports.forEach(r => {
      (r.pictures || []).forEach(async (gridfsId) => {
        if (loadedRef.current.has(gridfsId)) return;
        loadedRef.current.add(gridfsId);
        try {
          const res = await axios.get(`${API}/inventory/assets/${asset.id}/files/${gridfsId}`, { responseType: 'blob' });
          setPicUrls(prev => ({ ...prev, [gridfsId]: window.URL.createObjectURL(res.data) }));
        } catch { loadedRef.current.delete(gridfsId); }
      });
    });
  }, [asset.id, asset.damage_reports]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.date || !form.description) { toast.error(t('inv.damage.required')); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/inventory/assets/${asset.id}/damage-reports`, { ...form, repair_cost: form.repair_cost === '' ? null : parseFloat(form.repair_cost) });
      toast.success(t('inv.damage.added'));
      setAddOpen(false);
      setForm(emptyForm);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const openEdit = (r) => {
    setEditingReport(r);
    setForm({
      date: r.date || '',
      description: r.description || '',
      severity: r.severity || severityNames[0],
      reported_by: r.reported_by || '',
      repair_status: r.repair_status || statusNames[0],
      repair_notes: r.repair_notes || '',
      repair_cost: r.repair_cost != null ? String(r.repair_cost) : '',
      repair_currency: r.repair_currency || 'PLN',
    });
    setEditOpen(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.description) { toast.error(t('inv.damage.required')); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/inventory/assets/${asset.id}/damage-reports/${editingReport.id}`, { ...form, repair_cost: form.repair_cost === '' ? null : parseFloat(form.repair_cost) });
      toast.success(t('inv.damage.updated'));
      setEditOpen(false);
      setEditingReport(null);
      setForm(emptyForm);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const handleDelete = async (reportId) => {
    try { await axios.delete(`${API}/inventory/assets/${asset.id}/damage-reports/${reportId}`); toast.success(t('inv.damage.deleted')); onRefresh(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handlePhotoUpload = async (reportId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingFor(reportId);
    try {
      const fd = new FormData(); fd.append('file', file);
      await axios.post(`${API}/inventory/assets/${asset.id}/damage-reports/${reportId}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(t('inv.damage.photoUploaded')); onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || t('inv.common.uploadFailed')); }
    setUploadingFor(null);
  };

  const handleDeletePhoto = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/inventory/assets/${asset.id}/damage-reports/${deleteTarget.reportId}/pictures/${deleteTarget.gridfsId}`);
      toast.success(t('inv.damage.photoDeleted'));
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const openPhotoPreview = (gridfsId, reportId) => {
    setPreviewUrl(picUrls[gridfsId] || null);
    setPreviewPhotoMeta({ gridfsId, reportId });
    setPreviewOpen(true);
  };

  const openPhotoDeleteConfirm = (e, reportId, gridfsId) => {
    e.stopPropagation();
    setDeleteTarget({ reportId, gridfsId });
    setDeleteConfirmOpen(true);
  };

  const reports = asset.damage_reports || [];

  const renderForm = (onSubmit, submitLabel) => (
    <form onSubmit={onSubmit} className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label className="text-xs">{t('inv.common.date')} *</Label><Input type="date" value={form.date} onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value  }))} required /></div>
        <div>
          <Label className="text-xs">{t('inv.damage.severity')}</Label>
          <Select value={form.severity} onValueChange={(v) => setForm(prev => ({ ...prev, severity: v  }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{severityNames.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div><Label className="text-xs">{t('inv.common.description')} *</Label><Textarea value={form.description} onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value  }))} rows={2} required /></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">{t('inv.damage.reportedBy')}</Label>
          <Select value={form.reported_by || 'none'} onValueChange={(v) => setForm(prev => ({ ...prev, reported_by: v === 'none' ? '' : v  }))}>
            <SelectTrigger><SelectValue placeholder={t('inv.common.select')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t('inv.common.notSpecified')}</SelectItem>
              {employees.map(e => <SelectItem key={e.id} value={e.name}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">{t('inv.damage.repairStatus')}</Label>
          <Select value={form.repair_status} onValueChange={(v) => setForm(prev => ({ ...prev, repair_status: v  }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{statusNames.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div><Label className="text-xs">{t('inv.damage.repairNotes')}</Label><Input value={form.repair_notes} onChange={(e) => setForm(prev => ({ ...prev, repair_notes: e.target.value  }))} /></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label className="text-xs">{t('inv.damage.repairCost')}</Label><Input type="number" step="0.01" value={form.repair_cost} onChange={(e) => setForm(prev => ({ ...prev, repair_cost: e.target.value  }))} /></div>
        <div>
          <Label className="text-xs">{t('inv.common.currency')}</Label>
          <Select value={form.repair_currency} onValueChange={(v) => setForm(prev => ({ ...prev, repair_currency: v  }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{currencyList.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={() => { setAddOpen(false); setEditOpen(false); }} data-testid="btn-cancel">{t('common.cancel')}</Button>
        <Button type="submit" disabled={saving}>{saving ? t('inv.common.saving') : submitLabel}</Button>
      </div>
    </form>
  );

  return (
    <div className="space-y-3" data-testid="asset-damage-reports">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h4 className="text-sm font-semibold">{t('inv.damage.title', { count: reports.length })}</h4>
        {isAdmin && <Button size="sm" onClick={() => { setForm(emptyForm); setAddOpen(true); }} data-testid="add-damage-report-btn"><Plus className="w-4 h-4 mr-1" /> {t('inv.damage.report')}</Button>}
      </div>

      {reports.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">{t('inv.damage.empty')}</p>
      ) : (
        <div className="space-y-3">
          {reports.map((r) => (
            <div key={r.id} className="border rounded-lg p-3 space-y-2" data-testid={`damage-report-${r.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  <span className="text-sm font-medium">{r.date}</span>
                  <Badge variant={SEVERITY_COLORS[r.severity] || 'outline'} className="text-xs">{r.severity}</Badge>
                  <Badge variant={STATUS_COLORS[r.repair_status] || 'outline'} className="text-xs">{r.repair_status}</Badge>
                </div>
                {isAdmin && (
                  <div className="flex gap-1">
                    <input type="file" onChange={(e) => handlePhotoUpload(r.id, e)} className="hidden" accept="image/*" id={`dmg-photo-${r.id}`} />
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => document.getElementById(`dmg-photo-${r.id}`).click()} disabled={uploadingFor === r.id || (r.pictures || []).length >= 5} title={t('inv.damage.uploadCount', { count: (r.pictures || []).length })}><Upload className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(r)} data-testid={`edit-damage-${r.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete(r.id)} data-testid={`delete-damage-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                )}
              </div>
              <p className="text-sm">{r.description}</p>
              {r.reported_by && (
                <p className="text-xs text-muted-foreground">{t('inv.damage.reportedBy')}: {navigateTo ? (
                  <button className="text-blue-600 hover:underline" onClick={() => { onClose?.(); navigateTo('employees', { searchEmployee: r.reported_by }); }}>{r.reported_by}</button>
                ) : r.reported_by}</p>
              )}
              {r.repair_notes && <p className="text-xs text-muted-foreground">{t('inv.damage.repair')}: {r.repair_notes}</p>}
              {r.repair_cost > 0 && <p className="text-xs font-medium">{t('inv.damage.cost')}: {r.repair_cost.toLocaleString()} {r.repair_currency}</p>}
              {(r.pictures || []).length > 0 && (
                <div className="flex gap-2 flex-wrap mt-1">
                  {r.pictures.map((gid) => (
                    <div key={gid} className="relative group w-16 h-16 rounded border overflow-hidden" data-testid={`damage-photo-${gid}`}>
                      {picUrls[gid] ? (
                        <img
                          src={picUrls[gid]}
                          alt={t('auto.inv.damage')}
                          className="w-full h-full object-cover cursor-pointer"
                          onClick={() => openPhotoPreview(gid, r.id)}
                          data-testid={`damage-photo-preview-${gid}`}
                        />
                      ) : (
                        <div className="w-full h-full bg-muted" />
                      )}
                      <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-0.5 p-0.5 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button className="text-white p-0.5" onClick={() => openPhotoPreview(gid, r.id)}>
                          <Eye className="w-3 h-3" />
                        </button>
                        {isAdmin && (
                          <button className="text-red-300 p-0.5" onClick={(e) => openPhotoDeleteConfirm(e, r.id, gid)} data-testid={`delete-damage-photo-${gid}`}>
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-damage-report-dialog">
          <DialogHeader><DialogTitle><AlertTriangle className="w-4 h-4 inline mr-1 text-amber-500" />{t('inv.damage.report')}</DialogTitle><DialogDescription className="sr-only">{t('inv.damage.report')}</DialogDescription></DialogHeader>
          {renderForm(handleAdd, t('inv.damage.reportBtn'))}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-damage-report-dialog">
          <DialogHeader><DialogTitle><Pencil className="w-4 h-4 inline mr-1" />{t('inv.damage.edit')}</DialogTitle><DialogDescription className="sr-only">{t('inv.damage.edit')}</DialogDescription></DialogHeader>
          {renderForm(handleEdit, t('inv.common.update'))}
        </DialogContent>
      </Dialog>

      {/* Full-Size Photo Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl" data-testid="damage-photo-preview-dialog">
          <DialogHeader>
            <DialogTitle>{t('inv.damage.photoPreview')}</DialogTitle>
            <DialogDescription className="sr-only">{t('inv.damage.photoPreview')}</DialogDescription>
          </DialogHeader>
          {previewUrl && (
            <div className="flex items-center justify-center max-h-[70vh]">
              <img src={previewUrl} alt={t('auto.inv.damageFullPreview')} className="max-h-[65vh] max-w-full object-contain rounded" />
            </div>
          )}
          <DialogFooter className="flex justify-between">
            {isAdmin && previewPhotoMeta && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => { setPreviewOpen(false); setDeleteTarget(previewPhotoMeta); setDeleteConfirmOpen(true); }}
                data-testid="damage-preview-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" /> {t('common.delete')}
              </Button>
            )}
            <Button variant="outline" onClick={() => setPreviewOpen(false)} data-testid="dialog-close-btn">{t('common.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent data-testid="damage-photo-delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{t('inv.files.confirmDeletion')}</DialogTitle>
            <DialogDescription>{t('inv.damage.confirmPhotoDelete')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)} data-testid="dialog-cancel-btn">{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={handleDeletePhoto} data-testid="damage-confirm-delete-btn">{t('common.delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssetDamageReports;
